import { useCallback, useEffect, useRef, useState } from "react";
import { env } from "@/lib/env";

/** Whether the surveys server answers at all. Any answer of its own counts,
 *  including 401 or the SSO proxy's redirect; a failed fetch, a gateway
 *  status, or a 5xx without the server's JSON problem body is a proxy
 *  answering for a server that is down or starting. */
export async function isServerReachable(): Promise<boolean> {
    let res: Response;
    try {
        res = await fetch(`${env.apiBaseUrl}/v1/me`, { credentials: "include", redirect: "manual" });
    } catch {
        return false;
    }
    if (res.type === "opaqueredirect") return true;
    if (res.status === 502 || res.status === 503 || res.status === 504) return false;
    if (res.status >= 500) return (res.headers.get("content-type") ?? "").includes("json");
    return true;
}

/** Delay before the nth automatic retry (0-based): 2s, 4s, 8s, 16s, then 30s. */
export function retryDelayMs(attempt: number): number {
    return Math.min(2000 * 2 ** attempt, 30_000);
}

export interface ServerRecovery {
    checking: boolean;
    /** When the last check finished; null before the first. */
    lastTried: Date | null;
    /** Check now, outside the automatic schedule. */
    checkNow: () => void;
}

/** While `active`, checks the server on a backoff schedule and calls
 *  `onBack` once it answers. */
export function useServerRecovery(active: boolean, onBack: () => void): ServerRecovery {
    const [checking, setChecking] = useState(false);
    const [lastTried, setLastTried] = useState<Date | null>(null);
    const [attempt, setAttempt] = useState(0);
    const onBackRef = useRef(onBack);
    onBackRef.current = onBack;
    const inFlight = useRef(false);

    const check = useCallback(async () => {
        if (inFlight.current) return;
        inFlight.current = true;
        setChecking(true);
        const reachable = await isServerReachable();
        inFlight.current = false;
        setChecking(false);
        setLastTried(new Date());
        if (reachable) onBackRef.current();
        else setAttempt((n) => n + 1);
    }, []);

    useEffect(() => {
        if (!active) return;
        const timer = setTimeout(check, retryDelayMs(attempt));
        return () => clearTimeout(timer);
    }, [active, attempt, check]);

    return { checking, lastTried, checkNow: () => void check() };
}
